import { throttle } from "lodash";
import { useEffect, useState } from "react";

import { ResponsiveBreakpoint, ResponsiveBreakpointType } from "constants/responsive-breakpoint";

export interface IUseBreakpointsProps {
  breakpointType: ResponsiveBreakpointType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

/**
 * Checks and returns breakpoint type based on the current window width.
 *
 * Prefer using CSS media queries - use this hook only when you need to check the breakpoint in JS.
 * Note that this hook watches for resize events, values may be outdated if the window is resized but the event is not fired.
 *
 * @returns Current breakpoint type and boolean values for mobile, tablet and desktop.
 */
export const useBreakpoints = (): IUseBreakpointsProps => {
  const [currentWidth, setCurrentWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = throttle(() => {
      setCurrentWidth(window.innerWidth);
    }, 200);
    window.addEventListener("resize", handleResize);

    return (): void => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const isMobile = currentWidth <= ResponsiveBreakpoint.Mobile;
  const isTablet = !isMobile && currentWidth <= ResponsiveBreakpoint.Tablet;
  const isDesktop = !(isMobile || isTablet);
  const breakpointType =
    currentWidth <= ResponsiveBreakpoint.Mobile
      ? ResponsiveBreakpointType.Mobile
      : currentWidth <= ResponsiveBreakpoint.Tablet
        ? ResponsiveBreakpointType.Tablet
        : ResponsiveBreakpointType.Desktop;

  return {
    isMobile,
    isTablet,
    isDesktop,
    breakpointType,
  };
};
